import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link } from 'react-router-dom';
import React, { ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import './FirmwaresList.scss';
import { Breadcrumbs, ErrorMessage, Loader, Page, PageHeader } from 'components';
import { ErrorType, LoadState } from 'enums';
import { FirmwaresListItem } from './FirmwaresListItem';
import { DeviceInfoService, DevicesService } from 'services';
import { useCancelablePromise } from 'hooks';
import { IFirmware, IManufacturer } from 'interfaces';

export function FirmwaresList() {
    const { t, i18n } = useTranslation();
    const { cancelablePromise } = useCancelablePromise();

    const [firmwares, setFirmwares] = useState<IFirmware[]>([]);
    const [manufacturers, setManufacturers] = useState<{ [id: string]: IManufacturer; }>();
    const [infoLoadState, setInfoLoadState] = useState<LoadState>(LoadState.Loading);
    const [infoError, setInfoError] = useState('');

    // Load firmwares the first time we render
    useEffect(() => {
        cancelablePromise(DeviceInfoService.getManufacturerMap())
            .then(() => {
                setManufacturers(DeviceInfoService.allManufacturersById);
                setFirmwares(DeviceInfoService.allFirmwares);
                setInfoLoadState(LoadState.Success);
            })
            .catch((e) => {
                setInfoLoadState(LoadState.Error);
                setInfoError(e + '')
            });
    }, []);

    const tableHeaders = (
        <div className="firmwaresHeaders">
            <div className="firmwaresHeader firmwaresHeaders_addNew">
                <Link to="/firmware-versions/new" title={t('firmwaresList.headers.addNewTooltip')} data-cy="firmwares_addNewLink">
                    {t('firmwaresList.headers.addNew')}
                    <FontAwesomeIcon icon="plus" className="firmwaresHeaders_addNewIcon" />
                </Link>
            </div>
        </div>
    );

    let content: ReactNode;
    switch (infoLoadState) {
        case LoadState.Loading:
        case LoadState.Success:
            if (firmwares.length > 0 || infoLoadState === LoadState.Loading) {
                content = (<>
                    <ul className="firmwaresList" data-cy="firmwares_deviceList">
                        {firmwares.map(firmware => (
                            <FirmwaresListItem firmware={firmware} manufacturer={manufacturers[firmware.manufacturer_id].name} key={firmware.id} />
                        ))}
                    </ul>
                    {infoLoadState === LoadState.Loading && (
                        <Loader />
                    )}
                </>);
            } else {
                if (DevicesService.filters.length === 0) {
                    content = (
                        <div className="firmwaresList_emptyAddNew">
                            <div className="firmwaresList_addNewArrow">
                                <FontAwesomeIcon icon="reply" />
                            </div>
                            {t('firmwaresList.emptyMessageAddNew')}
                        </div>
                    );
                } 
            }
            break;
        case LoadState.Error:
            content = (
                <ErrorMessage type={ErrorType.Generic} message={infoError || DevicesService.loadErrorMessage} />
            );
            break;
    }

    const breadcrumbs = (
        <Breadcrumbs crumbs={[
            {
                label: t('devicesList.title'),
                url: '/',
            },
        ]} 
        dataCy="edit_breadcrumb"
        />
    );

    return (
        <Page>
            <PageHeader            
                top={breadcrumbs}
                title={t('firmwaresList.title')}
            />
            {tableHeaders}
            {content}
        </Page>
    );
}